import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { mapDealFromApi, stagePatchPayload } from '@/adapters/deals'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { Deal, DealReuniao, DealStage } from '@/types'
import { extractReuniaoFromApiResponse, ensureReuniao } from './reuniaoMeetingUtils'

const REALIZED_MEETING_STATUSES = new Set([
  'Realizada',
  'Concluída',
  'REALIZADA',
  'CONCLUÍDA',
])

async function patchDealStage(
  dealId: string,
  stage: DealStage,
  extra?: Record<string, unknown>,
) {
  const { data } = await api.patch(`/deals/${dealId}/stage`, stagePatchPayload(stage, extra))
  return mapDealFromApi(data.data ?? data)
}

async function markReuniaoRealizada(reuniaoId: string) {
  const { data } = await api.patch(`/reunioes/${reuniaoId}`, { status: 'Realizada' })
  const updated = extractReuniaoFromApiResponse(data)
  const status = updated?.status ?? 'Realizada'
  if (!REALIZED_MEETING_STATUSES.has(status)) {
    throw new Error('reuniao_confirm_failed')
  }
  return updated
}

export type BantSnapshot = Pick<Deal, 'budgetConfirmado' | 'decisorPresente' | 'doresIdentificadas'>

export function useReuniaoAgendadaActions(
  deal: Deal,
  reuniao: DealReuniao | undefined,
  onDealPatched: (updates: Partial<Deal>) => void,
) {
  const [confirming, setConfirming] = useState(false)
  const [rescheduling, setRescheduling] = useState(false)
  const [showNextStepModal, setShowNextStepModal] = useState(false)
  const [bantDraft, setBantDraft] = useState<BantSnapshot>({
    budgetConfirmado: deal.budgetConfirmado ?? '',
    decisorPresente: deal.decisorPresente ?? '',
    doresIdentificadas: deal.doresIdentificadas ?? deal.leadProblema ?? '',
  })

  const dealWithBant = useMemo(() => ({ ...deal, ...bantDraft }), [deal, bantDraft])

  const handleBantPatch = useCallback(
    (updates: Partial<Deal>) => {
      setBantDraft((prev) => ({
        budgetConfirmado: updates.budgetConfirmado ?? prev.budgetConfirmado,
        decisorPresente: updates.decisorPresente ?? prev.decisorPresente,
        doresIdentificadas: updates.doresIdentificadas ?? prev.doresIdentificadas,
      }))
      onDealPatched(updates)
    },
    [onDealPatched],
  )

  const handleConfirmarClick = useCallback(() => {
    if (!bantDraft.doresIdentificadas?.trim()) {
      toast.error('Preencha a observação para confirmar a reunião e avançar.')
      return
    }
    setShowNextStepModal(true)
  }, [bantDraft.doresIdentificadas])

  const finalizeConfirmacao = useCallback(
    async (goToProposta: boolean) => {
      setConfirming(true)
      setShowNextStepModal(false)
      try {
        const reuniaoId = await ensureReuniao(deal, reuniao)
        await markReuniaoRealizada(reuniaoId)

        const bantPayload = {
          budgetConfirmado: bantDraft.budgetConfirmado,
          decisorPresente: bantDraft.decisorPresente,
          doresIdentificadas: bantDraft.doresIdentificadas?.trim() ?? '',
        }

        await api.patch(`/deals/${deal.id}`, bantPayload)

        if (deal.etapa === 'qualificado') {
          await patchDealStage(deal.id, 'reuniao_agendada' as DealStage)
        }

        let finalDeal = await patchDealStage(
          deal.id,
          'reuniao_realizada' as DealStage,
          bantPayload,
        )

        if (goToProposta) {
          finalDeal = await patchDealStage(
            deal.id,
            'proposta_elaboracao' as DealStage,
            bantPayload,
          )
        }

        const updatedReuniao: DealReuniao = {
          ...(reuniao ?? {
            id: reuniaoId,
            data: deal.dataReuniao ?? new Date().toISOString(),
            tipo: 'Descoberta',
            closerId: deal.closerId,
            closerNome: deal.closerNome,
          }),
          status: 'Realizada',
        }

        onDealPatched({
          ...finalDeal,
          ...bantPayload,
          reunioes: [updatedReuniao, ...(deal.reunioes?.slice(1) ?? [])],
        })

        toast.success(
          goToProposta
            ? 'Reunião confirmada — proposta em elaboração liberada!'
            : 'Reunião confirmada.',
        )
      } catch (error: unknown) {
        if (error instanceof Error && error.message === 'missing_meeting') {
          toast.error('Nenhuma reunião encontrada para confirmar')
        } else if (error instanceof Error && error.message === 'missing_closer') {
          toast.error('Deal sem closer responsável — atualize o responsável antes de confirmar')
        } else if (error instanceof Error && error.message === 'reuniao_create_failed') {
          toast.error('Não foi possível registrar a reunião. Tente reagendar a data.')
        } else if (error instanceof Error && error.message === 'reuniao_confirm_failed') {
          toast.error('Não foi possível confirmar a reunião. Tente novamente.')
        } else {
          toast.error(getFriendlyError(error, 'Erro ao confirmar reunião'))
        }
      } finally {
        setConfirming(false)
      }
    },
    [deal, reuniao, bantDraft, onDealPatched],
  )

  const handleReagendar = useCallback(
    async (rescheduleAt: string, onRescheduled: () => void) => {
      if (!rescheduleAt) {
        toast.error('Selecione a nova data e hora')
        return
      }

      setRescheduling(true)
      try {
        const reuniaoId = await ensureReuniao(deal, reuniao)
        const isoDate = new Date(rescheduleAt).toISOString()

        await api.patch(`/reunioes/${reuniaoId}`, { data: isoDate, status: 'Agendada' })
        await api.patch(`/deals/${deal.id}`, { dataReuniao: isoDate } as Partial<Deal>)

        onDealPatched({
          dataReuniao: rescheduleAt,
          reunioes: [
            {
              ...(reuniao ?? {
                id: reuniaoId,
                tipo: 'Descoberta',
                status: 'Agendada',
                closerId: deal.closerId,
                closerNome: deal.closerNome,
              }),
              data: isoDate,
              status: 'Agendada',
            },
            ...(deal.reunioes?.slice(1) ?? []),
          ],
        })

        onRescheduled()
        toast.success('Reunião reagendada')
      } catch {
        toast.error('Erro ao reagendar reunião')
      } finally {
        setRescheduling(false)
      }
    },
    [deal, reuniao, onDealPatched],
  )

  return {
    bantDraft,
    dealWithBant,
    confirming,
    rescheduling,
    showNextStepModal,
    setShowNextStepModal,
    handleBantPatch,
    handleConfirmarClick,
    finalizeConfirmacao,
    handleReagendar,
  }
}
